import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiClient } from '../api/client';

type TemplateGroup = 'pipe' | 'binding';
type TemplateKind = 'pipe_pex' | 'pipe_pp' | 'binding_pex' | 'binding_pp';
type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

type DraftLine = {
  key: string;
  materialId: string;
  name: string;
  qty: string;
};

type MaterialHit = {
  id: string;
  name: string;
  pipe_type: string;
};

type ApiTemplate = {
  kind: string;
  items?: { material_id?: string; name?: string; default_qty?: number }[];
};

const GROUPS: Record<TemplateGroup, { kind: TemplateKind; label: string }[]> = {
  pipe: [
    { kind: 'pipe_pex', label: 'PEX' },
    { kind: 'pipe_pp', label: 'PP' },
  ],
  binding: [
    { kind: 'binding_pex', label: 'PEX' },
    { kind: 'binding_pp', label: 'PP' },
  ],
};

const KINDS: TemplateKind[] = ['pipe_pex', 'pipe_pp', 'binding_pex', 'binding_pp'];

const SAVE_ERROR_TEXT = 'Не удалось сохранить. Проверьте соединение и повторите.';
const QTY_ERROR = 'Введите количество больше 0: не больше 12 цифр и трёх знаков после запятой.';
const QTY_DIGIT_LIMIT = 12;
const QTY_FRACTION_LIMIT = 3;

const headerCellStyle = {
  textAlign: 'left' as const,
  padding: '10px 12px',
  borderBottom: '1px solid #e5e7eb',
  color: '#374151',
  fontWeight: 600,
};

const bodyCellStyle = {
  padding: '10px 12px',
  borderBottom: '1px solid #e5e7eb',
  verticalAlign: 'middle' as const,
};

function emptyDrafts(): Record<TemplateKind, DraftLine[]> {
  return {
    pipe_pex: [],
    pipe_pp: [],
    binding_pex: [],
    binding_pp: [],
  };
}

function isKind(value: string): value is TemplateKind {
  return KINDS.includes(value as TemplateKind);
}

function formatQty(qty: number): string {
  if (!Number.isFinite(qty) || qty <= 0) {
    return '';
  }

  return String(Math.round(qty * 1000) / 1000);
}

function lineFromApi(item: { material_id?: string; name?: string; default_qty?: number }): DraftLine | null {
  if (typeof item.material_id !== 'string' || typeof item.name !== 'string') {
    return null;
  }

  const qty = formatQty(Number(item.default_qty));
  if (!qty) {
    return null;
  }

  return {
    key: crypto.randomUUID(),
    materialId: item.material_id,
    name: item.name,
    qty,
  };
}

function readQty(draft: string): { qty: number; text: string } | null {
  let trimmed = draft.trim().replace(',', '.').replace(/\.$/, '');
  if (trimmed.startsWith('.')) {
    trimmed = `0${trimmed}`;
  }

  if (trimmed === '') {
    return null;
  }

  const match = /^(\d+)(?:\.(\d+))?$/.exec(trimmed);
  if (!match) {
    return null;
  }

  const intPart = match[1].replace(/^0+(?=\d)/, '');
  const fracPart = (match[2] ?? '').slice(0, QTY_FRACTION_LIMIT);
  const significant = `${intPart}${fracPart}`.replace(/^0+/, '');
  if (significant.length > QTY_DIGIT_LIMIT) {
    return null;
  }

  const text = fracPart.length > 0 ? `${intPart}.${fracPart}` : intPart;
  const qty = Number(text);
  if (!Number.isFinite(qty) || qty <= 0) {
    return null;
  }

  return { qty, text };
}

function sanitizeQtyInput(raw: string): string {
  const withoutMinus = raw.replace(/[\u2212\u2013\u2014-]/g, '');
  let separatorSeen = false;
  let intDigits = 0;
  let fracDigits = 0;
  let result = '';

  for (const char of withoutMinus) {
    if (char >= '0' && char <= '9') {
      if (separatorSeen) {
        if (fracDigits >= QTY_FRACTION_LIMIT || intDigits + fracDigits >= QTY_DIGIT_LIMIT) {
          continue;
        }

        fracDigits += 1;
        result += char;
        continue;
      }

      if (intDigits >= QTY_DIGIT_LIMIT) {
        continue;
      }

      intDigits += 1;
      result += char;
      continue;
    }

    if ((char === '.' || char === ',') && !separatorSeen) {
      separatorSeen = true;
      result += char;
    }
  }

  return result;
}

function isTransportError(error: unknown): boolean {
  if (!(error instanceof AxiosError)) {
    return true;
  }

  if (!error.response) {
    return true;
  }

  return error.response.status >= 500;
}

function errorText(error: unknown, fallback: string): string {
  if (!(error instanceof AxiosError)) {
    return fallback;
  }

  const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (item && typeof item === 'object' && 'msg' in item && typeof item.msg === 'string') {
          return item.msg.trim();
        }

        return '';
      })
      .filter((message) => message.length > 0);

    if (messages.length > 0) {
      return messages.join(' ');
    }
  }

  return fallback;
}

function readKind(group: TemplateGroup, value: string | null): TemplateKind {
  const match = GROUPS[group].find((option) => option.kind === value);
  return match ? match.kind : GROUPS[group][0].kind;
}

function saveStatusText(status: SaveStatus): string {
  if (status === 'saving') {
    return 'Сохранение…';
  }

  if (status === 'saved') {
    return 'Сохранено';
  }

  if (status === 'error') {
    return SAVE_ERROR_TEXT;
  }

  return '';
}

export function TemplateEditor({ group }: { group: TemplateGroup }) {
  const options = GROUPS[group];
  const [searchParams, setSearchParams] = useSearchParams();
  const kind = readKind(group, searchParams.get('kind'));
  const [drafts, setDrafts] = useState(emptyDrafts);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [tableError, setTableError] = useState<string | null>(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadTemplates() {
      setIsLoading(true);
      setLoadError(null);

      let unauthorized = false;

      try {
        const response = await apiClient.get<{ items?: ApiTemplate[] }>('/profile/templates');
        if (cancelled) {
          return;
        }

        const next = emptyDrafts();
        for (const template of response.data.items ?? []) {
          if (!isKind(template.kind)) {
            continue;
          }

          next[template.kind] = (template.items ?? []).flatMap((item) => {
            const line = lineFromApi(item);
            return line ? [line] : [];
          });
        }

        setDrafts(next);
      } catch (error) {
        if (cancelled) {
          return;
        }

        if (error instanceof AxiosError && error.response?.status === 401) {
          unauthorized = true;
          return;
        }

        setLoadError('Не удалось загрузить шаблоны. Проверьте соединение и повторите.');
      } finally {
        if (!cancelled && !unauthorized) {
          setIsLoading(false);
        }
      }
    }

    void loadTemplates();

    return () => {
      cancelled = true;
    };
  }, []);

  const lines = drafts[kind];

  function selectKind(next: TemplateKind) {
    const params = new URLSearchParams(searchParams);
    params.set('kind', next);
    setSearchParams(params, { replace: true });
    setSaveStatus('idle');
    setTableError(null);
  }

  function updateQty(key: string, raw: string) {
    setTableError(null);
    setDrafts((current) => ({
      ...current,
      [kind]: current[kind].map((line) => (line.key === key ? { ...line, qty: sanitizeQtyInput(raw) } : line)),
    }));
  }

  function removeLine(key: string) {
    const line = lines.find((item) => item.key === key);
    if (!line) {
      return;
    }

    const confirmed = window.confirm(`Убрать «${line.name}» из шаблона?`);
    if (!confirmed) {
      return;
    }

    setTableError(null);
    setDrafts((current) => ({
      ...current,
      [kind]: current[kind].filter((item) => item.key !== key),
    }));
  }

  function addLine(material: MaterialHit) {
    setTableError(null);
    setSaveStatus('idle');
    setDrafts((current) => ({
      ...current,
      [kind]: [
        ...current[kind],
        {
          key: crypto.randomUUID(),
          materialId: material.id,
          name: material.name,
          qty: '1',
        },
      ],
    }));
    setIsSearchOpen(false);
  }

  async function saveTemplate() {
    const items: { material_id: string; default_qty: number }[] = [];

    for (const line of lines) {
      const parsed = readQty(line.qty);
      if (!parsed) {
        setTableError(QTY_ERROR);
        setSaveStatus('idle');
        return;
      }

      items.push({ material_id: line.materialId, default_qty: parsed.qty });
    }

    setSaveStatus('saving');
    setTableError(null);

    try {
      const response = await apiClient.put<ApiTemplate>(`/profile/templates/${kind}`, { items });
      const saved = (response.data.items ?? []).flatMap((item) => {
        const line = lineFromApi(item);
        return line ? [line] : [];
      });
      setDrafts((current) => ({ ...current, [kind]: saved }));
      setSaveStatus('saved');
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 401) {
        return;
      }

      if (isTransportError(error)) {
        setSaveStatus('error');
        return;
      }

      setSaveStatus('idle');
      setTableError(errorText(error, 'Сервер отклонил шаблон.'));
    }
  }

  return (
    <section style={{ display: 'grid', gap: '12px' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }} role="group" aria-label="Тип трубы">
        {options.map((option) => (
          <button
            key={option.kind}
            type="button"
            className={option.kind === kind ? 'btn btn-active' : 'btn'}
            aria-pressed={option.kind === kind}
            disabled={saveStatus === 'saving'}
            onClick={() => selectKind(option.kind)}
          >
            {option.label}
          </button>
        ))}
      </div>

      <p
        aria-live="polite"
        style={{
          minHeight: '1.25em',
          margin: 0,
          color: saveStatus === 'error' ? '#b91c1c' : '#6b7280',
        }}
      >
        {saveStatusText(saveStatus)}
      </p>

      {isLoading ? <p style={{ margin: 0 }}>Загружаем шаблоны...</p> : null}

      {!isLoading && loadError ? (
        <p role="alert" style={{ color: '#b91c1c', margin: 0 }}>
          {loadError}
        </p>
      ) : null}

      {!isLoading && !loadError ? (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            <button type="button" className="btn" disabled={saveStatus === 'saving'} onClick={() => setIsSearchOpen(true)}>
              Добавить строку
            </button>
            <button type="button" className="btn" disabled={saveStatus === 'saving'} onClick={() => void saveTemplate()}>
              Сохранить шаблон
            </button>
          </div>

          <div
            style={{
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              background: '#ffffff',
              overflow: 'hidden',
            }}
          >
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={headerCellStyle}>Название</th>
                  <th style={headerCellStyle}>Количество</th>
                  <th style={headerCellStyle}>Действия</th>
                </tr>
              </thead>
              <tbody>
                {lines.length === 0 ? (
                  <tr>
                    <td colSpan={3} style={bodyCellStyle}>
                      Нет позиций. Добавьте первую запись.
                    </td>
                  </tr>
                ) : (
                  lines.map((line) => (
                    <tr key={line.key}>
                      <td style={bodyCellStyle}>{line.name}</td>
                      <td style={bodyCellStyle}>
                        <input
                          type="text"
                          inputMode="decimal"
                          autoComplete="off"
                          aria-label={`Количество: ${line.name}`}
                          value={line.qty}
                          disabled={saveStatus === 'saving'}
                          onChange={(event) => updateQty(line.key, event.target.value)}
                          style={{ width: '140px', padding: '8px' }}
                        />
                      </td>
                      <td style={bodyCellStyle}>
                        <button
                          type="button"
                          className="btn btn-icon btn-danger"
                          aria-label={`Удалить строку: ${line.name}`}
                          title="Удалить строку"
                          disabled={saveStatus === 'saving'}
                          onClick={() => removeLine(line.key)}
                        >
                          <TrashIcon />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {tableError ? (
            <p role="alert" style={{ color: '#b91c1c', margin: 0 }}>
              {tableError}
            </p>
          ) : null}
        </>
      ) : null}

      {isSearchOpen ? (
        <MaterialSearch onClose={() => setIsSearchOpen(false)} onPick={addLine} />
      ) : null}
    </section>
  );
}

function MaterialSearch({
  onClose,
  onPick,
}: {
  onClose: () => void;
  onPick: (material: MaterialHit) => void;
}) {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState<MaterialHit[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;

    async function loadMaterials() {
      setIsLoading(true);
      setLoadError(null);

      const params = new URLSearchParams();
      if (search) {
        params.set('search', search);
      }

      try {
        const response = await apiClient.get<{ items?: MaterialHit[] }>(`/profile/materials?${params.toString()}`);
        if (cancelled) {
          return;
        }

        setItems(Array.isArray(response.data.items) ? response.data.items : []);
      } catch (error) {
        if (cancelled) {
          return;
        }

        if (error instanceof AxiosError && error.response?.status === 401) {
          return;
        }

        setLoadError('Не удалось загрузить библиотеку. Проверьте соединение и повторите.');
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    void loadMaterials();

    return () => {
      cancelled = true;
    };
  }, [search]);

  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 30,
        background: 'rgba(17, 24, 39, 0.35)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '48px 16px',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Поиск по библиотеке"
        onClick={(event) => event.stopPropagation()}
        style={{
          width: 'min(480px, 100%)',
          display: 'grid',
          gap: '12px',
          padding: '16px',
          borderRadius: '12px',
          background: '#ffffff',
          border: '1px solid #e5e7eb',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: '18px' }}>Поиск по библиотеке</h2>
          <button type="button" className="btn" onClick={onClose}>
            Закрыть
          </button>
        </div>

        <input
          className="field"
          type="search"
          autoFocus
          placeholder="Название"
          aria-label="Поиск по названию"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          style={{ width: '100%' }}
        />

        {isLoading ? <p style={{ margin: 0 }}>Загружаем библиотеку...</p> : null}
        {!isLoading && loadError ? (
          <p role="alert" style={{ color: '#b91c1c', margin: 0 }}>
            {loadError}
          </p>
        ) : null}
        {!isLoading && !loadError && items.length === 0 ? (
          <p style={{ margin: 0 }}>{search ? 'Ничего не найдено' : 'Нет позиций'}</p>
        ) : null}
        {!isLoading && !loadError && items.length > 0 ? (
          <div style={{ display: 'grid', gap: '8px', maxHeight: '320px', overflow: 'auto' }}>
            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                className="btn"
                style={{ justifyContent: 'flex-start' }}
                onClick={() => onPick(item)}
              >
                {item.name} · {item.pipe_type}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function TrashIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6h18" />
      <path d="M8 6V4h8v2" />
      <path d="M19 6l-1 14H6L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
    </svg>
  );
}
